import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { calculatePriority } from '../domain/priority';
import {
  type FoodContainer,
  type InventorySnapshot,
  type QuantityUnit,
} from '../domain/model';
import { useI18n } from '../i18n/I18nContext';
import { demoContentLabel } from '../i18n/domain-labels';
import { messageFor, type AppLocale, type MessageKey } from '../i18n/catalog';

export const Icon = ({
  name,
  size = 22,
}: {
  name:
    | 'snow'
    | 'plus'
    | 'home'
    | 'box'
    | 'clock'
    | 'meal'
    | 'more'
    | 'search'
    | 'arrow'
    | 'move'
    | 'trash'
    | 'undo'
    | 'check'
    | 'warning'
    | 'close'
    | 'download'
    | 'upload'
    | 'settings'
    | 'audit'
    | 'info';
  size?: number;
}) => {
  const paths: Record<typeof name, ReactNode> = {
    snow: <path d="M12 2v20M4.2 6.5l15.6 11M19.8 6.5l-15.6 11M8.5 4l3.5 2 3.5-2M8.5 20l3.5-2 3.5 2" />,
    plus: <path d="M12 5v14M5 12h14" />,
    home: <path d="m3 11 9-8 9 8v10h-6v-6H9v6H3z" />,
    box: <path d="m4 7 8-4 8 4-8 4zM4 7v10l8 4 8-4V7M12 11v10" />,
    clock: <path d="M12 8v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" />,
    meal: <path d="M4 3v8a3 3 0 0 0 3 3V3M7 14v7M16 3v18M16 3c3 2 4 5 4 8h-4" />,
    more: <><circle cx="5" cy="12" r="1.2" /><circle cx="12" cy="12" r="1.2" /><circle cx="19" cy="12" r="1.2" /></>,
    search: <path d="m21 21-4.3-4.3M19 11a8 8 0 1 1-16 0 8 8 0 0 1 16 0" />,
    arrow: <path d="m9 18 6-6-6-6" />,
    move: <path d="M5 9 2 12l3 3M19 9l3 3-3 3M9 5l3-3 3 3M9 19l3 3 3-3M2 12h20M12 2v20" />,
    trash: <path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14M10 11v6M14 11v6" />,
    undo: <path d="M9 7 4 12l5 5M4 12h9a6 6 0 0 1 6 6" />,
    check: <path d="m4 12 5 5L20 6" />,
    warning: <><path d="M12 3 2 21h20z" /><path d="M12 9v5M12 18h.01" /></>,
    close: <path d="m6 6 12 12M18 6 6 18" />,
    download: <path d="M12 3v12M7 10l5 5 5-5M4 21h16" />,
    upload: <path d="M12 21V9M7 14l5-5 5 5M4 3h16" />,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.8 1.8 0 0 0 .4 2l.1.1-2.8 2.8-.1-.1a1.8 1.8 0 0 0-2-.4 1.8 1.8 0 0 0-1 1.7V21h-4v-.1a1.8 1.8 0 0 0-1-1.7 1.8 1.8 0 0 0-2 .4l-.1.1-2.8-2.8.1-.1a1.8 1.8 0 0 0 .4-2A1.8 1.8 0 0 0 3 14H3v-4h.1a1.8 1.8 0 0 0 1.7-1 1.8 1.8 0 0 0-.4-2l-.1-.1 2.8-2.8.1.1a1.8 1.8 0 0 0 2 .4A1.8 1.8 0 0 0 10 3V3h4v.1a1.8 1.8 0 0 0 1 1.7 1.8 1.8 0 0 0 2-.4l.1-.1 2.8 2.8-.1.1a1.8 1.8 0 0 0-.4 2A1.8 1.8 0 0 0 21 10h.1v4H21a1.8 1.8 0 0 0-1.6 1z" /></>,
    audit: <path d="M9 5h10v16H5V5h4M9 3h6v4H9zM8 12l2 2 4-4M8 18h8" />,
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7h.01" /></>,
  };
  return (
    <svg
      aria-hidden="true"
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
};

export const Button = ({
  variant = 'primary',
  icon,
  children,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  icon?: ReactNode;
}) => (
  <button className={`button button--${variant} ${className}`} {...props}>
    {icon}
    <span>{children}</span>
  </button>
);

export const Field = ({
  label,
  hint,
  error,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  error?: string;
}) => {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <label className="field" htmlFor={id}>
      <span className="field__label">{label}</span>
      <input
        {...props}
        id={id}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={error ? true : undefined}
      />
      {hint ? <span className="field__hint" id={hintId}>{hint}</span> : null}
      {error ? <span className="field__error" id={errorId}>{error}</span> : null}
    </label>
  );
};

export const SelectField = ({
  label,
  hint,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  hint?: string;
  children: ReactNode;
}) => {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  return (
    <label className="field" htmlFor={id}>
      <span className="field__label">{label}</span>
      <select {...props} id={id}>{children}</select>
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
};

export const Modal = ({
  title,
  description,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) => {
  const { t } = useI18n();
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
      if (event.key === 'Tab' && panelRef.current != null) {
        const focusable = [
          ...panelRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
          ),
        ].filter((element) => !element.hasAttribute('hidden'));
        const first = focusable[0];
        const last = focusable.at(-1);
        if (first == null || last == null) {
          event.preventDefault();
          panelRef.current.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div
        ref={panelRef}
        className={`modal ${wide ? 'modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <header className="modal__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('app.close')}>
            <Icon name="close" />
          </button>
        </header>
        <div className="modal__body">{children}</div>
      </div>
    </div>
  );
};

export const EmptyState = ({
  icon = 'snow',
  title,
  children,
  action,
}: {
  icon?: Parameters<typeof Icon>[0]['name'];
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) => (
  <div className="empty-state">
    <span className="empty-state__icon"><Icon name={icon} size={30} /></span>
    <h3>{title}</h3>
    <p>{children}</p>
    {action}
  </div>
);

const interpolate = (message: string, parameters: Readonly<Record<string, string | number>> = {}): string =>
  message.replace(/\{([A-Za-z0-9_]+)\}/g, (placeholder, key: string) =>
    parameters[key] == null ? placeholder : String(parameters[key]));

export const unitLabel = (unit: QuantityUnit, locale: AppLocale, count = 2): string => {
  if (!['piece', 'portion', 'contenant'].includes(unit)) return messageFor(locale, `unit.${unit}` as MessageKey);
  const pluralLocale = locale === 'en-XA' ? 'fr-FR' : locale;
  const suffix = new Intl.PluralRules(pluralLocale).select(count) === 'one' ? 'one' : 'other';
  return messageFor(locale, `unit.${unit}.${suffix}` as MessageKey);
};

export const categoryLabel = (categoryId: string, locale: AppLocale): string =>
  messageFor(locale, `category.${categoryId}` as MessageKey);

export const containerReference = (container: FoodContainer, locale: AppLocale): string =>
  interpolate(
    messageFor(locale, container.serialNumber == null ? 'common.itemWithoutNumber' : 'common.number'),
    { number: container.serialNumber ?? '' },
  );

export const containerDisplayName = (snapshot: InventorySnapshot, container: FoodContainer, locale: AppLocale): string =>
  demoContentLabel(container.name, locale, snapshot.meta.demoSeededAt != null);

export const snapshotDisplayLabel = (snapshot: InventorySnapshot, value: string, locale: AppLocale): string =>
  demoContentLabel(value, locale, snapshot.meta.demoSeededAt != null);

export const formatQuantity = (container: FoodContainer, locale: AppLocale = 'fr-FR'): string =>
  container.remainingQuantity == null || container.unit == null
    ? messageFor(locale, 'quantity.missing')
    : `${new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(container.remainingQuantity)} ${unitLabel(container.unit, locale, container.remainingQuantity)}`;

export const formatDate = (value: string | null, locale: AppLocale = 'fr-FR'): string =>
  value == null
    ? messageFor(locale, 'common.notProvided')
    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(`${value}T12:00:00`));

export const locationLabel = (snapshot: InventorySnapshot, locationId: string, locale: AppLocale = 'fr-FR'): string => {
  const byId = new Map(snapshot.locations.map((location) => [location.id, location]));
  const location = byId.get(locationId);
  if (location == null) return messageFor(locale, 'common.unknownLocation');
  const parts = [snapshotDisplayLabel(snapshot, location.name, locale)];
  let cursor = location;
  let guard = 0;
  while (cursor.parentId != null && guard < 3) {
    const parent = byId.get(cursor.parentId);
    if (parent == null) break;
    parts.unshift(snapshotDisplayLabel(snapshot, parent.name, locale));
    cursor = parent;
    guard += 1;
  }
  const freezer = snapshot.freezers.find((item) => item.id === location.freezerId);
  if (freezer != null) parts.unshift(snapshotDisplayLabel(snapshot, freezer.name, locale));
  return parts.join(' › ');
};

export const statusLabel = (status: FoodContainer['status'], locale: AppLocale = 'fr-FR'): string =>
  messageFor(locale, `status.${status}` as MessageKey);

export const ContainerCard = ({
  container,
  snapshot,
  onClick,
  onExit,
}: {
  container: FoodContainer;
  snapshot: InventorySnapshot;
  onClick: () => void;
  onExit?: () => void;
}) => {
  const { locale, t } = useI18n();
  const priority = calculatePriority(container, new Date().toISOString().slice(0, 10));
  const displayName = containerDisplayName(snapshot, container, locale);
  const isFridgeItem = container.trackingMode === 'internal-only';
  const hasSeveral = (container.remainingQuantity ?? container.initialQuantity ?? 1) > 1;
  return (
    <div className="container-card">
      <button
        className="container-card__open"
        type="button"
        onClick={onClick}
        aria-label={
          container.serialNumber == null
            ? t('container.openItem', { reference: containerReference(container, locale), name: displayName })
            : t('container.openNumber', { number: container.serialNumber, name: displayName })
        }
      >
        {container.serialNumber == null ? null : (
          <span className="serial-badge" aria-label={t('container.numberAria', { number: container.serialNumber })}>{container.serialNumber}</span>
        )}
        <span className="container-card__body">
          <span className="container-card__title">{displayName}</span>
          <span className="container-card__meta">
            {categoryLabel(container.categoryId, locale)}{t('common.separator')}{formatQuantity(container, locale)}
          </span>
          <span className="container-card__meta">{locationLabel(snapshot, container.locationId, locale)}</span>
          {isFridgeItem && (container.openedOn != null || container.useFirstOn != null) ? (
            <span className="container-card__meta">
              {container.openedOn != null ? t('common.openedOn', { date: formatDate(container.openedOn, locale) }) : ''}
              {container.openedOn != null && container.useFirstOn != null ? t('common.separator') : ''}
              {container.useFirstOn != null ? t('common.referenceOn', { date: formatDate(container.useFirstOn, locale) }) : ''}
            </span>
          ) : null}
          <span className={`priority-chip priority-chip--${priority.level}`}>{t(`priority.${priority.level}` as MessageKey)}</span>
        </span>
        <Icon name="arrow" />
      </button>
      {onExit != null && ['present', 'partial'].includes(container.status) ? (
        <div className="container-card__actions">
          <button className="container-card__exit" type="button" onClick={onExit} aria-label={isFridgeItem ? t('container.consumeAria', { name: displayName }) : t('container.exitAria', { number: container.serialNumber ?? '' })}>
            {isFridgeItem ? t('container.consumed') : t('container.exit')}
          </button>
          {isFridgeItem && hasSeveral ? (
            <button className="container-card__adjust" type="button" onClick={onClick}>
              {t('container.adjustQuantity')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export const UnitOptions = () => {
  const { locale, t } = useI18n();
  const units: QuantityUnit[] = ['g', 'kg', 'ml', 'l', 'portion', 'piece', 'contenant'];
  return (
    <>
      <option value="">{t('unit.choose')}</option>
      {units.map((value) => <option key={value} value={value}>{unitLabel(value, locale)}</option>)}
    </>
  );
};

export const SectionHeader = ({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) => (
  <div className="section-header">
    <div>
      {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
      <h1>{title}</h1>
      {description ? <p>{description}</p> : null}
    </div>
    {action}
  </div>
);
